/**
 * How a block picks up data an undo restored, without being destroyed to see it.
 *
 * Undo used to bump `undoRevision` — a React `key` — which rebuilds the whole editor. That is how
 * restored data reached a block that had snapshotted `block.data` at mount. Three defects came out
 * of the rebuild: the options drawer lost its state, a ring lost the element it was on, and a
 * feedback card folded itself shut. Each was fixed by compensating for it.
 *
 * All fourteen block types are off it now and the remount is gone, along with the ladder module
 * that tracked which ones had moved. This is the part every one of them shares. What they do NOT
 * share is the re-seeding: numerical holds one state object, keyword seven plus a ref, slider
 * thirty-five. That is the `apply` callback, and it stays with the block.
 *
 * Two rules are encoded here, and both were learned the expensive way in `numerical-block.tsx`:
 *
 * **Only the editing instance holds state.** Every block is two mounted instances at once — the
 * collapsed card in the canvas list and the editor panel — and both used to keep their own copy.
 * The echo bookkeeping below is unsound with two: whichever instance emitted marks the data as its
 * own and declines to re-derive, while the other re-derives from data the first is mid-edit on.
 * So the card reads `data` straight from props and keeps no state to go stale.
 *
 * **The marker is STATE, not a ref.** This is the one that does not look like a bug. React can
 * start a render, be interrupted by a higher-priority update, and restart it from the last
 * committed state: that discards the render-phase re-seed but NOT a ref mutated on the way past.
 * The marker then reads "already seen" for data the block never applied, and no later render can
 * correct it — a permanently stale editor, not a flicker. It presented as a Tiptap bug (an undo of
 * typed text left the text on screen) and was misdiagnosed as one twice.
 */
import { useMemo, useState } from 'react';

import { recordUndoEvent } from '@/lib/undo-recorder';

export interface RestoredData<T> {
  /** What this block should be showing: its own state while editing, the props otherwise. */
  data: T;
  /**
   * Record what this block just sent upward, so its own echo is not mistaken for someone else's
   * change. Call it wherever the block calls `onUpdate`.
   */
  markEmitted: (sent: unknown) => void;
}

export interface RestoredDataOptions<T> {
  /** `block.data`, exactly as the prop arrives. */
  raw: unknown;
  /** Only the editing instance keeps state; the other derives every render. */
  isEditing: boolean;
  /** `block.data` in the shape this block's editor works in — its `initData`, normally. */
  derive: (raw: unknown) => T;
  /**
   * What the block is showing right now, for the deep compare below. Optional, and worth passing:
   * without it a write that `derive` does not even carry — a block healing its own bookkeeping —
   * re-seeds every control on the surface, including a rich-text editor being typed into.
   */
  current?: T;
  /** Put restored data back into the block's own state. Called during render, so keep it to setters. */
  apply: (next: T) => void;
}

/**
 * `current` has to describe the SAME fields as `data`, or the compare below is blind where they
 * differ — and blind means the block quietly declines to show what an undo restored.
 *
 * Caught the hard way on keyword, whose question lived in a ref rather than in state: leaving it
 * out of `current` (as `''`) made typing into an otherwise untouched block compare equal, so the
 * re-seed never ran and an undo moved the data without moving the editor. Nothing failed; the
 * editor just went on showing text that had been taken back.
 */
function assertComparable(data: unknown, current: unknown): void {
  const keys = (v: unknown) => Object.keys((v ?? {}) as object).sort().join(',');
  if (keys(data) === keys(current)) return;
  throw new Error(
    `useRestoredData: \`current\` must carry the same fields as \`derive\` returns.\n` +
    `  derive: ${keys(data)}\n  current: ${keys(current)}`,
  );
}

export function useRestoredData<T>({ raw, isEditing, derive, current, apply }: RestoredDataOptions<T>): RestoredData<T> {
  /*
   * KEYED ON THE PROP'S IDENTITY, because this runs for every block on the page on every
   * render of any of them.
   *
   * `handleUpdateBlock` replaces one block's `data` and leaves every other reference alone,
   * so `raw` is stable between that block's own edits — and serialising it fresh each time
   * cost a keystroke the whole task. Measured against the dev database: 79kB of block data
   * across a 29-block task, two mounted instances each, re-serialised per render. Reported
   * as "the system seems to become a bit sluggish".
   */
  const incoming = useMemo(() => JSON.stringify(raw), [raw]);
  /**
   * What this block is answerable for: `now` is the data it last applied or sent, `before` the data
   * it was on immediately prior.
   *
   * `before` is what makes a write in flight safe. A block emits and the prop does not necessarily
   * come back in the same commit — the parent may defer it, or decline it. On the render in
   * between, the prop still holds the PRE-write data, which is neither the echo nor anyone else's
   * change; treating it as a change re-seeds the block backwards, undoing the teacher's own edit a
   * frame after they made it. React's batching hides this today, since a handler's `setState` and
   * the parent's land together, which is exactly the kind of accident worth not relying on.
   *
   * And `before` is dropped the moment the echo lands, which is not optional: an UNDO restores
   * exactly the data the block was on before its write, so a `before` kept indefinitely makes every
   * undo of a single edit indistinguishable from a write still in flight. Toggling a switch and
   * undoing it left the switch on.
   */
  const [seen, setSeen] = useState<{ now: string; before: string | null }>({ now: incoming, before: null });
  // Entering the editor seeds state from the props the card has been deriving from all along.
  // Without it the panel opens showing whatever the data said when this instance mounted.
  const [wasEditing, setWasEditing] = useState(isEditing);
  if (wasEditing !== isEditing) setWasEditing(isEditing);

  // Keyed on the prop, not on `derive`, which is a fresh closure every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = useMemo(() => derive(raw), [raw]);

  if (import.meta.env.DEV && current !== undefined) assertComparable(data, current);

  const mine = incoming === seen.now || incoming === seen.before;
  if (!mine || (isEditing && !wasEditing)) {
    /**
     * Only the decisions that could matter. Recording every render would bury the one that did.
     *
     * `applied` false alongside `mine` false is the failure to look for: the block was handed data
     * it had not seen and declined to show it, which is an undo that moves the data and not the
     * editor.
     *
     * THE SECOND HALF USED TO BE `!wasEditing`, which is every COLLAPSED CARD on every render:
     * `wasEditing` is synced to `isEditing` on the line above, so a card's is permanently
     * false. That is the opposite of what the first line of this comment says, and it cost
     * twice over — two whole-data serialisations per card per render to build `applied`, and
     * a recorder flooded with one entry per card per render, which it then writes to
     * localStorage by serialising its own two-thousand-entry buffer.
     *
     * The decision worth recording is the editor OPENING, which is the one moment `wasEditing`
     * genuinely differs and the re-seed below can fire on something other than `!mine`.
     */
    recordUndoEvent('derive', {
      isEditing, wasEditing, mine,
      applied: isEditing && (current === undefined || JSON.stringify(data) !== JSON.stringify(current)),
      incoming: incoming.length, sameAsNow: incoming === seen.now, sameAsBefore: incoming === seen.before,
    });
  }
  if (mine && seen.before !== null && incoming === seen.now) {
    // The write landed. Stop excusing the data it replaced.
    setSeen({ now: seen.now, before: null });
  }
  if (isEditing && (!wasEditing || !mine)) {
    setSeen({ now: incoming, before: null });
    if (current === undefined || JSON.stringify(data) !== JSON.stringify(current)) apply(data);
  } else if (!isEditing && !mine) {
    // The card applies nothing — it has nothing to apply to — but it still has to keep the marker
    // current, or entering the editor would look like a change that needs re-seeding.
    setSeen({ now: incoming, before: null });
  }

  return {
    data,
    markEmitted: (sent) => setSeen(prev => ({ now: JSON.stringify(sent), before: prev.now })),
  };
}
